import { QuestionCircleOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import Popconfirm from 'antd/es/popconfirm';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { MessageErrorIcon, OrderIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxCardTitle } from 'components/RevxCardTitle';
import { ORDER_HISTORY_DATE_FORMAT, RevxDate } from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxMoney } from 'components/RevxMoney';
import { Text, UnderlineText } from 'components/RevxTypography';
import { CartLineItem } from 'domains/cartDomain';
import { OrderHistoryItem, OrderStatusEnum } from 'domains/orderDomain';
import isEmpty from 'lodash-es/isEmpty';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import OrderHistoryLineItem from './OrderHistoryLineItem';
import { orderHistoryTabsKey } from '../OrderHistoryLayout';

type Props = {
  data: OrderHistoryItem;
  orderType: number;
};
export const OrderHistoryItemContainer = ({ data, orderType }: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const placingOrder = useAppSelector(
    (state) => state.loading.effects.order.getPurchaseJob
  );
  const payOrderLoadId = useAppSelector((state) => state.order.payOrderLoadId);

  const payForOrder = useCallback(async () => {
    await dispatch.order.getPurchaseJob({
      ...data,
      id: data.id,
      jobIds: data.jobs.map((job) => job.jobId),
      status: data.status,
    });
    history.push(`/shopping-cart/purchase-order/${data.id}`);
  }, [data, dispatch.order, history]);

  const cancelOrder = useCallback(async () => {
    dispatch.order.cancelOrder(data.id);
  }, [data.id, dispatch.order]);

  const paymentFailure = useMemo(() => {
    // first item will be the latest payment message
    if (!isEmpty(data.orderPayments) && !data.orderPayments[0].success) {
      return data.orderPayments[0].message;
    } else {
      return undefined;
    }
  }, [data.orderPayments]);

  return (
    <>
      <div>
        <RevxCardTitle
          className={classNames(
            'order-history-title',
            paymentFailure && 'error'
          )}
        >
          <RevxFlexLayout justify="space-between" style={{ width: '100%' }}>
            <div>
              <OrderIcon
                style={{ marginRight: 16, position: 'relative', top: -1 }}
              />
              <span>
                {t('orderHistory.order')}: {data.id}
              </span>
            </div>

            <Space>
              {paymentFailure && (
                <RevxFlexLayout>
                  <MessageErrorIcon
                    style={{
                      marginRight: 12,
                      color: '#f3739e',
                      width: 18,
                      height: 18,
                    }}
                  />
                  <Text className="message">
                    {t('orderHistory.paymentFailed')}: {paymentFailure}
                  </Text>
                </RevxFlexLayout>
              )}
              {data.status === OrderStatusEnum.AWAITING_PAYMENT && (
                <>
                  <Popconfirm
                    placement="top"
                    title={t('common.confirmQuestion')}
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    onConfirm={() => cancelOrder()}
                    okText={t('common.yes')}
                    cancelText={t('common.no')}
                  >
                    <UnderlineText
                      color="blue"
                      size="small"
                      style={{
                        marginRight:
                          data.status === OrderStatusEnum.AWAITING_PAYMENT
                            ? 0
                            : 40,
                      }}
                    >
                      {t('orderHistory.cancelOrder')}
                    </UnderlineText>{' '}
                  </Popconfirm>

                  <RevxButton
                    hidden={orderType === orderHistoryTabsKey.ORG_ORDER}
                    type="primary"
                    style={{ marginLeft: 24, marginRight: 40 }}
                    loading={payOrderLoadId === data.id ? placingOrder : false}
                    onClick={payForOrder}
                  >
                    {t('orderHistory.payForOrder')}
                  </RevxButton>
                </>
              )}
              {data.status === OrderStatusEnum.CANCELLED && (
                <Text
                  style={{ color: '#757575', marginLeft: 24, marginRight: 40 }}
                >
                  {t('orderHistory.orderCanceled')}
                </Text>
              )}
            </Space>
          </RevxFlexLayout>
        </RevxCardTitle>
        <div className="order-history-tax">
          <RevxFlexLayout justify="space-between" style={{ width: '100%' }}>
            <label>
              <div>
                {t('orderHistory.createDate')}:{' '}
                <RevxDate
                  dateStr={data.createAt}
                  formatStr={ORDER_HISTORY_DATE_FORMAT}
                />
              </div>
              <div>
                {t('orderHistory.purchaseDate')}:{' '}
                <RevxDate
                  dateStr={data.updateAt}
                  formatStr={ORDER_HISTORY_DATE_FORMAT}
                />
              </div>
            </label>
            <div className="order-history-tax-price">
              <span>
                {data.jobs.length}{' '}
                {data.jobs.length > 1 ? t('shopcart.jobs') : t('shopcart.job')}:
              </span>

              <span>
                {t('workspace.detail.shippingBilling.printing')}:{' '}
                <RevxMoney value={data.printingCharge} />
              </span>
              <span>
                {t('workspace.detail.shippingBilling.freight')}:{' '}
                <RevxMoney value={data.freightCharge} />
              </span>
              <span>
                {t('workspace.detail.shippingBilling.tax')}:{' '}
                <RevxMoney value={data.taxCharge} />
              </span>

              <label>
                {t('workspace.detail.shippingBilling.orderTotal')}:{' '}
                <RevxMoney value={data.totalCharge} />
              </label>
            </div>
          </RevxFlexLayout>
        </div>
      </div>
      <div className="order-history-line-items">
        {data.jobs.map((item: CartLineItem) => {
          return (
            <OrderHistoryLineItem
              data={item}
              key={item.jobId}
              orderStatus={data.status}
              jobContacts={item.jobContacts}
            />
          );
        })}
      </div>
    </>
  );
};
